import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import tasksService, { CreateTaskDto } from '../../services/tasks.service';
import casesService, { Case } from '../../services/cases.service';
import adminService, { User } from '../../services/admin.service';

const TaskForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cases, setCases] = useState<Case[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const [formData, setFormData] = useState<CreateTaskDto & { status?: string }>({
    case_id: 0,
    title: '',
    description: '',
    type: 'other',
    priority: 'normal',
    assigned_to: undefined,
    due_date: ''
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [casesResult, usersResult] = await Promise.all([
        casesService.list({ limit: 100 }),
        adminService.listUsers()
      ]);
      
      setCases(casesResult.data);
      setUsers(usersResult.filter(u => u.is_active));

      if (isEdit && id) {
        const task = await tasksService.getById(parseInt(id));
        setFormData({
          case_id: task.case_id,
          title: task.title,
          description: task.description || '',
          type: task.type,
          priority: task.priority,
          assigned_to: task.assigned_to,
          due_date: task.due_date ? task.due_date.split('T')[0] : '',
          status: task.status
        });
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.case_id || !formData.title) {
      toast.error('Por favor complete los campos requeridos');
      return;
    }

    try {
      setSaving(true);

      const dataToSend = {
        ...formData,
        assigned_to: formData.assigned_to || undefined,
        due_date: formData.due_date || undefined
      };

      if (isEdit && id) {
        await tasksService.update(parseInt(id), dataToSend);
        toast.success('Tarea actualizada');
      } else {
        await tasksService.create(dataToSend);
        toast.success('Tarea creada');
      }

      navigate('/tasks');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al guardar la tarea');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-4">
        <Button variant="link" className="p-0 me-3" onClick={() => navigate('/tasks')}>
          <FiArrowLeft size={24} />
        </Button>
        <h1 className="h3 mb-0">{isEdit ? 'Editar Tarea' : 'Nueva Tarea'}</h1>
      </div>

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Caso *</Form.Label>
                  <Form.Select
                    name="case_id"
                    value={formData.case_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Seleccione un caso</option>
                    {cases.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.case_number} - {c.client_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Título *</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Título de la tarea"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Descripción</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Descripción detallada de la tarea"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Tipo</Form.Label>
                  <Form.Select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                  >
                    <option value="drafting">Redacción</option>
                    <option value="filing">Radicación</option>
                    <option value="research">Investigación</option>
                    <option value="meeting">Reunión</option>
                    <option value="court">Juzgado</option>
                    <option value="other">Otro</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Prioridad</Form.Label>
                  <Form.Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                  >
                    <option value="low">Baja</option>
                    <option value="normal">Normal</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Fecha de Vencimiento</Form.Label>
                  <Form.Control
                    type="date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Asignar a</Form.Label>
                  <Form.Select
                    name="assigned_to"
                    value={formData.assigned_to || ''}
                    onChange={handleChange}
                  >
                    <option value="">Sin asignar</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.full_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {isEdit && (
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Estado</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status || ''}
                      onChange={handleChange}
                    >
                      <option value="pending">Pendiente</option>
                      <option value="in_progress">En Progreso</option>
                      <option value="completed">Completada</option>
                      <option value="cancelled">Cancelada</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
            </Row>

            <div className="d-flex justify-content-end mt-4 gap-2">
              <Button variant="secondary" onClick={() => navigate('/tasks')}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <FiSave className="me-2" />
                    {isEdit ? 'Actualizar' : 'Crear'} Tarea
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default TaskForm;
